<script setup lang="ts">
import SettingsIcon from '@/components/icons/SettingsIcon.vue';
import CreditCardIcon from '@/components/icons/CreditCardIcon.vue';
import BankNoteIcon from '@/components/icons/BankNoteIcon.vue';
import NotificationBoxIcon from '@/components/icons/NotificationBoxIcon.vue';
import TicketIcon from '@/components/icons/TicketIcon.vue';
import UsersIcon from '@/components/icons/UsersIcon.vue';
import ListIcon from '@/components/icons/ListIcon.vue';
import { isObject } from 'lodash';

const props = defineProps({
	initialTab: {
		type: String,
		default: null,
	},
});

const tabs = [
	{
		title: mappedSettingTabTitle[SETTING_TABS.ACCOUNT_SETTING],
		name: SETTING_TABS.ACCOUNT_SETTING,
		icon: SettingsIcon,
	},
	{
		title: mappedSettingTabTitle[SETTING_TABS.PAYOUTS],
		name: SETTING_TABS.PAYOUTS,
		icon: BankNoteIcon,
	},
	{
		title: mappedSettingTabTitle[SETTING_TABS.SAVED_CARDS],
		name: SETTING_TABS.SAVED_CARDS,
		icon: CreditCardIcon,
	},
	{
		title: mappedSettingTabTitle[SETTING_TABS.NOTIFICATION_SETTINGS],
		name: SETTING_TABS.NOTIFICATION_SETTINGS,
		icon: NotificationBoxIcon,
	},
	{
		title: mappedSettingTabTitle[SETTING_TABS.TICKET_PURCHASE_HISTORY],
		name: SETTING_TABS.TICKET_PURCHASE_HISTORY,
		icon: TicketIcon,
	},
	{
		title: mappedSettingTabTitle[SETTING_TABS.FOLLOWING],
		name: SETTING_TABS.FOLLOWING,
		icon: UsersIcon,
	},
	{
		title: mappedSettingTabTitle[SETTING_TABS.CONTACTS],
		name: SETTING_TABS.CONTACTS,
		icon: ListIcon,
	},
];

const route = useRoute();
const router = useRouter();
const profileStore = useProfileStore();
const { isTablet } = useWindowResize();

const activeTabQuery = ref<string>(props.initialTab || SETTING_TABS.ACCOUNT_SETTING);

function handleDropdownChange(selectedValue: string) {
	activeTabQuery.value = selectedValue;
}

function handleTabChange(tabName: string) {
	router.push({
		name: route.name,
		query: { tab: tabName },
	});
}

function handleClickTab(tabName: string) {
	activeTabQuery.value = tabName;
}

watch(() => props.initialTab, (newTab) => {
	if (newTab) {
		activeTabQuery.value = newTab.toString();
	}
});

watch(() => route.query?.tab, (newQuery) => {
	if (newQuery) {
		activeTabQuery.value = String(newQuery) || SETTING_TABS.ACCOUNT_SETTING;
	}
});

watch(() => activeTabQuery.value, (newTab) => {
	if (newTab) {
		handleTabChange(newTab);
	}
});

onMounted(async () => {
	if (!isObject(route.query) && !profileStore.profileId) {
		await profileStore.myAccountInfo();
	}
	// If the user is redirected from Stripe to the settings page,
	// automatically switch to the payouts tab
	if (route.query?.[CONNECT_STRIPE_STATUS_QUERY_NAME.CONNECT_STATUS]) {
		activeTabQuery.value = SETTING_TABS.PAYOUTS;
	} else if (route.query?.tab) {
		activeTabQuery.value = String(route.query.tab) || SETTING_TABS.ACCOUNT_SETTING;
	}
});
</script>
<template>
  <div class="page settings-page-container">
    <template v-if="isTablet">
      <AccountSettingMobileMenu
        :selected="activeTabQuery"
        :settings="tabs"
        @on-change="handleDropdownChange"
      />
    </template>
    <template v-else>
      <VTabs
        v-model="activeTabQuery"
        class="settings-menu"
        direction="vertical"
        centered
      >
        <VTab
          v-for="tab in tabs"
          :key="tab.name"
          :value="tab.name"
          :ripple="false"
          @click="handleClickTab(tab.name)"
        >
          <component
            :is="tab.icon"
            :color="activeTabQuery === tab.name ? '#3538CD' : '#667085'"
            width="24"
            height="24"
          />
          {{ tab.title }}
        </VTab>
      </VTabs>
    </template>
    <VWindow
      v-model="activeTabQuery"
      class="settings-content"
      direction="vertical"
    >
      <VWindowItem
        :transition="false"
        :reverse-transition="false"
        :value="SETTING_TABS.ACCOUNT_SETTING"
      >
        <AccountSettingProfileForm />
      </VWindowItem>
      <VWindowItem
        :transition="false"
        :reverse-transition="false"
        :value="SETTING_TABS.PAYOUTS"
      >
        <AccountSettingPayoutForm />
      </VWindowItem>
      <VWindowItem
        :transition="false"
        :reverse-transition="false"
        :value="SETTING_TABS.SAVED_CARDS"
      >
        <AccountSettingSavedCardForm />
      </VWindowItem>
      <VWindowItem
        :transition="false"
        :reverse-transition="false"
        :value="SETTING_TABS.NOTIFICATION_SETTINGS"
      >
        <AccountSettingNotificationForm />
      </VWindowItem>
      <VWindowItem
        :transition="false"
        :reverse-transition="false"
        :value="SETTING_TABS.TICKET_PURCHASE_HISTORY"
      >
        <AccountSettingTicketHistory v-if="activeTabQuery === SETTING_TABS.TICKET_PURCHASE_HISTORY" />
      </VWindowItem>
      <VWindowItem
        :transition="false"
        :reverse-transition="false"
        :value="SETTING_TABS.FOLLOWING"
      >
        <AccountSettingFollowingForm v-if="activeTabQuery === SETTING_TABS.FOLLOWING" />
      </VWindowItem>
      <VWindowItem
        :transition="false"
        :reverse-transition="false"
        :value="SETTING_TABS.CONTACTS"
      >
        <AccountSettingContactForm v-if="activeTabQuery === SETTING_TABS.CONTACTS" />
      </VWindowItem>
    </VWindow>
  </div>
</template>
<style lang="scss" scoped>
.settings-page-container {
  display: flex;
  flex-direction: row;
  gap: spacings-get(8);
  max-width: rem(1200);

  @include media-query-max(tablet) {
    flex-direction: column;
    padding-bottom: spacings-get(12);
    padding-top: 0;
  }

  .settings-menu, .settings-content {
    border: 1px solid colors-get(gray, 200);
    @include border-radius-lg;
  }

  .settings-menu {
    height: fit-content;
    padding: spacings-get(3);
  }

  .settings-content {
    width: 100%;
    height: fit-content;
    padding: spacings-get(6);

    @include media-query-max(tablet) {
      padding: 0 spacings-get(4);
      border: none;
      border-radius: 0;
    }
  }
}
</style>